import {
    IconBuildingSkyscraper,
    IconExternalLink,
    IconFileDescription,
    IconFileShredder,
    IconGauge,
    IconList,
    IconLock,
    IconLogout,
    IconMail,
    IconRobot,
    IconUsersGroup,
    IconBook,
} from '@tabler/icons-react';
import {AppShell, Divider, Text} from '@mantine/core';
import {useEffect, useState} from 'react';
import {useNavigate} from 'react-router-dom';

import {ERoutes} from 'src/types/enum/routes.enum';
import {MainLinkProps} from 'src/types/interfaces/main-link';
import {MainLinks} from './_mainLinks';
import {userProfileActions} from 'src/store/reducers/user-reducer';
import {authActions} from 'src/store/reducers/auth-reducer';
import {useClearStore} from 'src/store';
import {useUserService} from 'src/services';
import {User} from 'src/types/models/User';

const adminLinks: MainLinkProps[] = [
    {icon: <IconList size="1rem" />, color: 'blue', label: 'Executions', path: ERoutes.EXECUTIONS},
    {icon: <IconFileShredder size="1rem" />, color: 'orange', label: 'Files Cleanup Config', path: ERoutes.ADMIN_FILES_CONFIG},
    {
        icon: <IconBuildingSkyscraper size="1rem" />,
        color: 'green',
        label: 'Organizations',
        path: ERoutes.ADMIN_ORGANIZATIONS,
    },
    {icon: <IconFileDescription size="1rem" />, color: 'violet', label: 'Terms Of Service', path: ERoutes.ADMIN_TOS},
    {icon: <IconUsersGroup size="1rem" />, color: 'violet', label: 'Users', path: ERoutes.USERS},
    {icon: <IconRobot size="1rem" />, color: 'violet', label: 'Agents', path: ERoutes.ADMIN_AGENTS},
];

const userLinks: MainLinkProps[] = [
    {icon: <IconList size="1rem" />, color: 'blue', label: 'Executions', path: ERoutes.EXECUTIONS},
    {icon: <IconFileDescription size="1rem" />, color: 'violet', label: 'Terms Of Service', path: ERoutes.USER_READ_TOS},
];

const organizationManagerLinks: MainLinkProps[] = [
    {icon: <IconList size="1rem" />, color: 'blue', label: 'Executions', path: ERoutes.EXECUTIONS},
    {icon: <IconFileDescription size="1rem" />, color: 'violet', label: 'Terms Of Service', path: ERoutes.TOS},
    {icon: <IconUsersGroup size="1rem" />, color: 'violet', label: 'Users', path: ERoutes.USERS},
];

const docsLink: MainLinkProps = {
    icon: <IconBook size="1rem" />,
    color: 'teal',
    label: 'Documentation',
    path: '',
    external: true,
    href: 'https://docs.qperfect.io',
    rightSection: <IconExternalLink size="0.9rem" />,
    confirmBeforeOpen: true,
};

export function NavbarContent({onNavigate}: {onNavigate?: () => void}) {
    const userProfile = userProfileActions.useUserProfile();
    const token = authActions.useToken();
    const clearStore = useClearStore();
    const navigate = useNavigate();
    const userService = useUserService();
    const [currentUser, setCurrentUser] = useState<User | null>(null);

    useEffect(() => {
        if (token) {
            userService.getProfile({
                error: () => { /* no-op */ },
                success: res => {
                    setCurrentUser(res);
                },
            });
        }
    }, [token]);

    const accountLinks: MainLinkProps[] = [
        {
            icon: <IconGauge size="1rem" />,
            color: 'green',
            label: 'Usage & Limits',
            path: ERoutes.ACCOUNT,
        },
        {
            icon: <IconLock size="1rem" />,
            color: 'blue',
            label: 'Change Password',
            path: ERoutes.CHANGE_PASSWORD,
        },
        {
            icon: <IconMail size="1rem" />,
            color: 'cyan',
            label: 'Contact Us',
            path: '',
            useLocationHref: true,
            href: 'mailto:mimiq.support@qperfect.io',
        },
        {
            icon: <IconLogout size="1rem" />,
            color: 'red',
            label: 'Logout',
            path: '',
            onAction: async () => {
                await clearStore();
                navigate(ERoutes.SIGN_IN);
            },
        },
    ];

    return (
        <AppShell.Section grow mt="md" style={{display: 'flex', flexDirection: 'column'}}>
            <div style={{flex: 1}}>
                {userProfile.isAdmin && <MainLinks links={adminLinks} onNavigate={onNavigate} />}
                {userProfile.isUser && <MainLinks links={userLinks} onNavigate={onNavigate} />}
                {userProfile.isOrganizationManager && <MainLinks links={organizationManagerLinks} onNavigate={onNavigate} />}
                <MainLinks links={[docsLink]} onNavigate={onNavigate} />
            </div>
            {token && (
                <div>
                    <Divider my="sm" />
                    {currentUser && (
                        <div style={{padding: '4px 12px 8px'}}>
                            <Text fw={600} size="sm" truncate>
                                {currentUser.firstName} {currentUser.lastName}
                            </Text>
                            <Text size="xs" c="dimmed" truncate>
                                {currentUser.email}
                            </Text>
                            {currentUser.organization?.name && (
                                <Text size="xs" c="dimmed" truncate>
                                    {currentUser.organization.name}
                                </Text>
                            )}
                        </div>
                    )}
                    <MainLinks links={accountLinks} onNavigate={onNavigate} />
                </div>
            )}
        </AppShell.Section>
    );
}

// Keep default export for backward compat
export function AppNavbar() {
    return <NavbarContent />;
}
